import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { MaxViewProvider } from "src/contexts";
import { legacyHasSelectedAduOption } from "src/utils/summary.utils";
import { HBLoadingSpinner } from "~components";
import {
  SummaryPrintConfiguration_CheckoutConfigFragment,
  SummaryPrintConfiguration_EstimatedInvoiceScheduleItemFragment,
  useSummaryPrintConfigurationQuery,
} from "./SummaryPrint.page.generated";
import { AduReport } from "./components/Adu.report";
import { DisclaimerReport } from "./components/Disclaimer.report";
import { EstimatedInvoiceScheduleReport } from "./components/EstimatedInvoiceSchedule.report";
import { ExteriorReport } from "./components/Exterior.report";
import { FloorPlanReport } from "./components/FloorPlan.report";
import { InteriorReport } from "./components/Interior.report";
import { SummaryReport } from "./components/Summary.report";

export const PrintSummaryReports = {
  summary: { label: "Summary receipt", Component: SummaryReport },
  floorPlans: { label: "Floor Plan", Component: FloorPlanReport },
  exterior: { label: "Exterior", Component: ExteriorReport },
  interior: { label: "Interior", Component: InteriorReport },
  adu: { label: "ADU", Component: AduReport },
  // Controlled separately via the "Estimated Invoice Schedule" checkbox in PDFPrintModal (not shown in the standard report checkbox list)
  estimatedInvoiceSchedule: { label: "Estimated Invoice Schedule", Component: EstimatedInvoiceScheduleReport },
};

export type PrintSummaryReportSlugs = keyof typeof PrintSummaryReports;

export function SummaryPrintPage({ print = true }: { print?: boolean }) {
  const { checkoutConfigId } = useParams<{ checkoutConfigId: string }>();
  const [searchParams] = useSearchParams();

  let reports = (searchParams.get("reports")?.split(",") || []) as PrintSummaryReportSlugs[];
  const shouldDisplayPricing = JSON.parse(searchParams.get("includePricing") || "false");
  const additionalSitePriceInCents = searchParams.get("additionalSitePriceInCents")
    ? Number(searchParams.get("additionalSitePriceInCents"))
    : undefined;

  const { data, loading } = useSummaryPrintConfigurationQuery({
    variables: { checkoutConfigId: checkoutConfigId ?? "", additionalSitePriceInCents },
    skip: !checkoutConfigId,
  });

  if (loading) {
    return <HBLoadingSpinner />;
  }

  if (!data?.checkoutConfig) {
    return <div>Configuration not found</div>;
  }

  // If we got the adu report, make sure we actually have something to show first.
  // Its a pretty common occurence to copy+paste a report url changing only the ccfgID and seeing a random ADU FP is
  // a problem since these reports are attached to contracts signed by homeowners.
  const hasAduReport =
    !!data.checkoutConfig.aduProductOffering || legacyHasSelectedAduOption(data.checkoutConfig.optionGroups);
  if (reports.includes("adu") && !hasAduReport) {
    reports = reports.filter((report) => report !== "adu");
  }

  const invoiceCategoryNames = Object.fromEntries(
    data.enumDetails.costCodeInvoiceCategory.map((d) => [d.code, d.name]),
  );

  return (
    <SummaryPrintPageView
      reports={reports}
      data={data.checkoutConfig}
      estimatedInvoiceSchedule={data.checkoutConfig.estimatedInvoiceSchedule}
      invoiceCategoryNames={invoiceCategoryNames}
      shouldDisplayPricing={shouldDisplayPricing}
      print={print}
    />
  );
}

type SummaryPrintPageViewProps = {
  data: SummaryPrintConfiguration_CheckoutConfigFragment;
  reports: PrintSummaryReportSlugs[];
  shouldDisplayPricing: boolean;
  estimatedInvoiceSchedule?: SummaryPrintConfiguration_EstimatedInvoiceScheduleItemFragment[];
  invoiceCategoryNames?: Record<string, string>;
  // This is just used for testing
  print?: boolean;
};

export function SummaryPrintPageView({
  data,
  reports,
  shouldDisplayPricing,
  estimatedInvoiceSchedule,
  invoiceCategoryNames,
  print = true,
}: SummaryPrintPageViewProps) {
  const [hasPrinted, setHasPrinted] = useState(false);

  /** We must ensure all assets are fully loaded before calling `window.print()` to avoid missing images or floor plan SVGs,
   *  as the browser snapshots what's rendered at the point the print dialog is opened. */
  useEffect(() => {
    if (print && !hasPrinted) {
      // Wait for all resources to load
      const waitForAllAssets = () => {
        // Check if all images are loaded
        const images = Array.from(document.querySelectorAll("img"));
        const allImagesLoaded = images.every((img) => img.complete);

        // Unlike images, we don't have a `complete` equivalent to check the loaded status of the SVG markup that we load via a `fetch` call
        // Since we only have a single floorplan SVG file, we can use this simple check of "is the SVG element in the DOM when including the floorPlans report?"
        // If that changes, we may need a more sophisticated way to supervise the loaded state of all FloorPlan children
        const isRenderingFloorPlanReport = reports.includes("floorPlans");
        const svgElements = Array.from(document.querySelectorAll("svg"));
        const maybeSvgLoaded = isRenderingFloorPlanReport ? svgElements.nonEmpty : true;

        // Check if any fetch operations are still pending
        // We'll use a small delay to ensure React has finished rendering
        if (allImagesLoaded && maybeSvgLoaded) {
          // Small delay to ensure layout is stable and all React effects have run
          setTimeout(() => {
            if (!hasPrinted) {
              setHasPrinted(true);
              window.print();
            }
          }, 200);
        } else {
          // Check again in a bit
          setTimeout(waitForAllAssets, 100);
        }
      };

      if (document.readyState === "complete") {
        waitForAllAssets();
      } else {
        window.addEventListener("load", waitForAllAssets);
        return () => window.removeEventListener("load", waitForAllAssets);
      }
    }
  }, [print, hasPrinted, reports]);

  return (
    // NOTE: FIXME: This should not be here. We placed useMaxView inside our `ImageDisplay` component.
    // That'll be getting replaced with a separate component will use the `useModal` provider that's at the root level of our app.
    <MaxViewProvider>
      {reports.map((report) => {
        const { Component } = PrintSummaryReports[report];
        return (
          Component && (
            <Component
              data={data}
              key={report}
              shouldDisplayPricing={shouldDisplayPricing}
              estimatedInvoiceSchedule={estimatedInvoiceSchedule}
              invoiceCategoryNames={invoiceCategoryNames}
            />
          )
        );
      })}
      <DisclaimerReport data={data} />
    </MaxViewProvider>
  );
}
